import React, { useEffect, useState } from "react";

import { BadgeCheck, Check, Copy, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

const FORMAT_LABEL_KEYS = {
  mso_mdoc: "walletOffer:format_mso_mdoc",
  ldp_vc: "walletOffer:format_ldp_vc",
};

const getInitials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

const IssuerLogo = ({ logoUrl, issuerName, alt }) => {
  const [hasError, setHasError] = useState(false);
  const showImage = logoUrl && !hasError;

  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {showImage ? (
        <img
          src={logoUrl}
          alt={alt}
          className="h-full w-full object-contain p-1.5"
          onError={() => setHasError(true)}
        />
      ) : (
        <span
          className="flex h-full w-full items-center justify-center bg-cfTableHeaderBackgroundColor text-lg font-semibold text-cfPrimary"
          role="img"
          aria-label={alt}
        >
          {getInitials(issuerName)}
        </span>
      )}
    </div>
  );
};

const COPY_FEEDBACK_MS = 2000;

const CredentialIdRow = ({ credentialId }) => {
  const { t } = useTranslation(["walletOffer"]);
  const [copyState, setCopyState] = useState("idle");
  const canCopy =
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.writeText === "function";

  useEffect(() => {
    if (copyState === "idle") return undefined;
    const timeoutId = setTimeout(() => setCopyState("idle"), COPY_FEEDBACK_MS);
    return () => clearTimeout(timeoutId);
  }, [copyState]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(credentialId);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const isCopied = copyState === "copied";

  return (
    <div className="mt-[1rem] flex items-center justify-between gap-3 border-t border-dashed border-gray-200 pt-[0.875rem]">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
          {t("walletOffer:credential_id_label")}
        </p>
        <p className="mt-0.5 break-all font-mono text-sm font-medium text-cfHeadingText">
          {credentialId}
        </p>
      </div>
      {canCopy ? (
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={`text-xs font-medium ${
              copyState === "failed" ? "text-red-600" : "text-cfPrimary"
            }`}
            aria-live="polite"
          >
            {isCopied ? t("walletOffer:credential_id_copied") : null}
            {copyState === "failed"
              ? t("walletOffer:credential_id_copy_failed")
              : null}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={t("walletOffer:copy_credential_id")}
            title={t("walletOffer:copy_credential_id")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-[1px] border-gray-200 bg-white text-gray-500 transition-colors hover:border-cfPrimaryBorder hover:bg-cfPrimaryMuted hover:text-cfPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-cfPrimary" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const IssuerSummary = ({
  issuer = {},
  credentialName,
  credentialFormat,
  credentialId,
}) => {
  const { t } = useTranslation(["walletOffer"]);
  const issuerName = issuer.name || "";
  const formatKey = FORMAT_LABEL_KEYS[credentialFormat];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <IssuerLogo
          logoUrl={issuer.logo_url}
          issuerName={issuerName}
          alt={t("walletOffer:issuer_logo_alt", { issuerName })}
        />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            {t("walletOffer:issued_by")}
          </p>
          <p className="truncate text-base font-semibold text-cfHeadingText">
            {issuerName}
          </p>
          {issuer.website_url ? (
            <a
              href={issuer.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0.5 inline-flex items-center gap-1 rounded text-sm font-medium text-cfPrimary hover:text-cfPrimaryDark hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2"
            >
              {t("walletOffer:visit_issuer_website")}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-[1rem] sm:p-[1.25rem]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cfPrimary">
          {t("walletOffer:credential_label")}
        </p>
        <h2 className="mt-1 break-words text-xl font-bold leading-snug text-cfHeadingText sm:text-2xl">
          {credentialName}
        </h2>
        {formatKey ? (
          <span className="mt-[0.75rem] inline-flex items-center gap-1.5 rounded-full bg-cfTableHeaderBackgroundColor px-[0.75rem] py-1 text-xs font-medium text-cfPrimary">
            <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {t(formatKey)}
          </span>
        ) : null}
        {credentialId ? <CredentialIdRow credentialId={credentialId} /> : null}
      </div>
    </div>
  );
};

export default IssuerSummary;
